import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import BrandIntelligence from "./pages/BrandIntelligence";
import NotFound from "./pages/NotFound";
import RankTracker from "./pages/RankTracker";
import KeywordTrends from "./pages/KeywordTrends";
import BrandContext from "./pages/BrandContext";
import KeywordLandscapeOutput from "./pages/KeywordLandscapeOutput";
import ABTests from "./pages/ABTests";
import CreateABTest from "./pages/CreateABTest";
import SearchTerms from "./pages/SearchTerms";
import ReportPreview from "./pages/ReportPreview";
import Profile from "./pages/Profile";
import SettingsPage from "./pages/Settings";
import SignalPerformanceIndex from "./pages/SignalPerformanceIndex";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import ProductSeoInsightsPage from "./pages/ProductSeoInsightsPage";
import ProductKeywordsPage from "./pages/ProductKeywordsPage";
import ProductLandscapesPage from "./pages/ProductLandscapesPage";
import ProductAbTestsPage from "./pages/ProductAbTestsPage";
import ProductSearchTermsPage from "./pages/ProductSearchTermsPage";
import ProductContentScorecardPage from "./pages/ProductContentScorecardPage";
import DeveloperHandoff from "./pages/DeveloperHandoff";
import SignalGuide from "./pages/SignalGuide";
import AdminPanelRedirect from "@/pages/AdminPanelRedirect";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SignUp from "./pages/SignUp";
import AcceptInvitation from "./pages/AcceptInvitation";
import PublicShareView from "./pages/PublicShareView";
import queryClient from "./query-client";
import { RoutePath } from "@/routes";
import ProtectedRoute from "@/components/ProtectedRoute";
import GuestRoute from "@/components/GuestRoute";
import AdminRoute from "@/components/AdminRoute";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />

      <Sonner />

      <BrowserRouter>
        <Routes>
            <Route element={<GuestRoute />}>
              <Route path={RoutePath.Login} element={<Login />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              {/* AUT-245 §1: Brand Intelligence is the new primary landing page. Brand
                  Overview (Dashboard) stays reachable via the sidebar. */}
              <Route path={RoutePath.Home} element={<Navigate to={RoutePath.BrandIntelligence} replace />} />
              <Route path={RoutePath.Dashboard} element={<Dashboard />} />
              {/* AUT-245: Brand Intelligence. Reachable now; the `/` -> BrandIntelligence
                  home flip + hiding Brand Overview happens once the tabs have real content. */}
              <Route path={RoutePath.BrandIntelligence} element={<BrandIntelligence />} />
              <Route path={RoutePath.RankTracker} element={<RankTracker />} />
              <Route path={RoutePath.KeywordTrends} element={<KeywordTrends />} />
              <Route element={<AdminRoute />}>
                <Route path={RoutePath.BrandContext} element={<BrandContext />} />
              </Route>
              <Route
                path={RoutePath.KeywordLandscapeDetail}
                element={<KeywordLandscapeOutput />}
              />
              <Route path={RoutePath.ABTests} element={<ABTests />} />
              <Route path={RoutePath.CreateABTest} element={<CreateABTest />} />
              <Route path={RoutePath.SearchTerms} element={<SearchTerms />} />
              <Route
                path={RoutePath.SPI}
                element={<SignalPerformanceIndex />}
              />
              <Route path={RoutePath.Products} element={<Products />} />
              <Route path={RoutePath.ProductDetail} element={<ProductDetail />}>
                <Route index element={<Navigate to="seo-insights" replace />} />
                <Route path="seo-insights" element={<ProductSeoInsightsPage />} />
                <Route path="keywords" element={<ProductKeywordsPage />} />
                <Route path="landscapes" element={<ProductLandscapesPage />} />
                <Route path="landscapes/:landscapeId" element={<ProductLandscapesPage />} />
                <Route path="ab-tests" element={<ProductAbTestsPage />} />
                <Route path="search-terms" element={<ProductSearchTermsPage />} />
                <Route path="content-scorecard" element={<ProductContentScorecardPage />} />
              </Route>
              <Route
                path={RoutePath.ReportPreview}
                element={<ReportPreview />}
              />
              <Route
                path={RoutePath.ReportDetail}
                element={<ReportPreview />}
              />
              <Route path={RoutePath.Profile} element={<Profile />} />
              <Route element={<AdminRoute />}>
                <Route path={RoutePath.Settings} element={<SettingsPage />} />
              </Route>
              <Route
                path={RoutePath.DeveloperHandoff}
                element={<DeveloperHandoff />}
              />
              <Route path={RoutePath.SignalGuide} element={<SignalGuide />} />
              <Route path={RoutePath.AdminPanel} element={<AdminPanelRedirect />} />
            </Route>

          <Route path={RoutePath.AcceptInvitation} element={<AcceptInvitation />} />

          <Route path={RoutePath.PublicShare} element={<PublicShareView />} />

          {/* AUT-39: WEB-002/WEB-003. Not inside <GuestRoute> -- see RoutePath.ForgotPassword's comment. */}
          <Route path={RoutePath.ForgotPassword} element={<ForgotPassword />} />
          <Route path={RoutePath.ResetPassword} element={<ResetPassword />} />

          {/* AUT-39: WEB-005. Also bare, not <GuestRoute> -- /signup realizes
              no journey and must not be blocked, redirected or turned into
              an error in any auth state (account-journeys.md § Built, but
              not a journey). GuestRoute would bounce a visitor holding a
              non-expired cookie to `/`, which is exactly the redirect that
              section forbids. */}
          <Route path={RoutePath.SignUp} element={<SignUp />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>

    <ReactQueryDevtools buttonPosition="bottom-left" />
  </QueryClientProvider>
);

export default App;
